/**
 * Redux actions for the Batch Tables (Tabular Review) feature.
 *
 * Naming: "BatchTables" to avoid collision with existing TablesAction.tsx
 * which handles the SSE table-generation feature.
 */

import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';
import axios from 'axios';
import apis, { fetchWithSSE } from '../../../utils/apis';
import { showFeedbackModal } from '../UserFeedbackActions';
import { RootState } from '../..';
import {
  CreateTablePayload,
  UpdateTablePayload,
  CreateColumnPayload,
  UpdateColumnPayload,
  SaveAsTemplatePayload,
  CreateTemplatePayload,
} from '../../../types/batchTables';
import { config } from '../../../utils/config';

// ─── Action Types ────────────────────────────────────────────────────────────

export const BATCH_TABLES_LOADING = 'BATCH_TABLES_LOADING';
export const BATCH_TABLES_FETCH_LIST = 'BATCH_TABLES_FETCH_LIST';
export const BATCH_TABLES_APPEND_LIST = 'BATCH_TABLES_APPEND_LIST';
export const BATCH_TABLES_SET_DETAIL = 'BATCH_TABLES_SET_DETAIL';
export const BATCH_TABLES_LOADING_DETAIL = 'BATCH_TABLES_LOADING_DETAIL';
export const BATCH_TABLES_UPDATE_IN_LIST = 'BATCH_TABLES_UPDATE_IN_LIST';
export const BATCH_TABLES_REMOVE_FROM_LIST = 'BATCH_TABLES_REMOVE_FROM_LIST';
export const BATCH_TABLES_SET_TEMPLATES = 'BATCH_TABLES_SET_TEMPLATES';
export const BATCH_TABLES_LOADING_TEMPLATES = 'BATCH_TABLES_LOADING_TEMPLATES';
export const BATCH_TABLES_SET_UPLOADING = 'BATCH_TABLES_SET_UPLOADING';
export const BATCH_TABLES_SET_PROCESSING = 'BATCH_TABLES_SET_PROCESSING';
export const BATCH_TABLES_ERROR = 'BATCH_TABLES_ERROR';
export const BATCH_TABLES_UPDATE_CELL = 'BATCH_TABLES_UPDATE_CELL';
export const BATCH_TABLES_UPDATE_ROW_STATUS = 'BATCH_TABLES_UPDATE_ROW_STATUS';
export const BATCH_TABLES_UPDATE_TABLE_STATUS = 'BATCH_TABLES_UPDATE_TABLE_STATUS';
export const BATCH_TABLES_RESET_COLUMN_CELLS = 'BATCH_TABLES_RESET_COLUMN_CELLS';

// SSE Creation
export const BATCH_TABLE_CREATE_RESET = 'BATCH_TABLE_CREATE_RESET';
export const BATCH_TABLE_CREATE_CONNECTING = 'BATCH_TABLE_CREATE_CONNECTING';
export const BATCH_TABLE_CREATE_CONNECTED = 'BATCH_TABLE_CREATE_CONNECTED';
export const BATCH_TABLE_CREATE_STEPS = 'BATCH_TABLE_CREATE_STEPS';
export const BATCH_TABLE_CREATE_STEP = 'BATCH_TABLE_CREATE_STEP';
export const BATCH_TABLE_CREATE_COMPLETED = 'BATCH_TABLE_CREATE_COMPLETED';
export const BATCH_TABLE_CREATE_FAILED = 'BATCH_TABLE_CREATE_FAILED';

// Cell Comments
export const BATCH_TABLES_SET_CELL_COMMENTS = 'BATCH_TABLES_SET_CELL_COMMENTS';
export const BATCH_TABLES_LOADING_CELL_COMMENTS = 'BATCH_TABLES_LOADING_CELL_COMMENTS';
export const BATCH_TABLES_ADD_CELL_COMMENT = 'BATCH_TABLES_ADD_CELL_COMMENT';
export const BATCH_TABLES_UPDATE_CELL_COMMENT = 'BATCH_TABLES_UPDATE_CELL_COMMENT';
export const BATCH_TABLES_REMOVE_CELL_COMMENT = 'BATCH_TABLES_REMOVE_CELL_COMMENT';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const handleApiError = (
  error: any,
  dispatch: ThunkDispatch<RootState, undefined, AnyAction>,
  customMessage?: string
) => {
  // Skip cancelled requests (includes licensing errors converted to Cancel by interceptor)
  if (axios.isCancel(error)) return;

  const response = error?.response?.data || {};
  const message = customMessage || response.error || response.message || 'Something went wrong. Please try again.';

  dispatch(showFeedbackModal({
    showModal: true,
    message,
    modalType: 'error',
    duration: 5000,
  }));

  dispatch({ type: BATCH_TABLES_ERROR, payload: message });
};

// ─── Table CRUD ──────────────────────────────────────────────────────────────

interface FetchTablesParams {
  page?: number;
  page_size?: number;
  matter_id?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  search_term?: string;
}

export const fetchBatchTables = (params: FetchTablesParams = {}) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    const { page = 1, page_size = 20, matter_id, sort_by, sort_order, search_term } = params;

    dispatch({ type: BATCH_TABLES_LOADING, payload: true });

    try {
      const response = await apis.get('/tables', {
        params: {
          page,
          page_size,
          ...(matter_id ? { matter_id } : {}),
          ...(sort_by ? { sort_by } : {}),
          ...(sort_order ? { sort_order } : {}),
          ...(search_term ? { search_term } : {}),
        },
      });

      if (response.status === 200) {
        const data = response.data;
        dispatch({
          type: page === 1 ? BATCH_TABLES_FETCH_LIST : BATCH_TABLES_APPEND_LIST,
          payload: {
            items: data.items || [],
            total: data.total || 0,
            page: data.page || page,
            total_pages: data.total_pages || 1,
          },
        });
        return data;
      }
    } catch (error) {
      handleApiError(error, dispatch);
    } finally {
      dispatch({ type: BATCH_TABLES_LOADING, payload: false });
    }

    return null;
  };
};

export const fetchBatchTableDetail = (tableUuid: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    if (!tableUuid) return null;

    dispatch({ type: BATCH_TABLES_LOADING_DETAIL, payload: true });

    try {
      const response = await apis.get(`/tables/${tableUuid}`);

      if (response.status === 200) {
        dispatch({ type: BATCH_TABLES_SET_DETAIL, payload: response.data });
        return response.data;
      }
    } catch (error) {
      handleApiError(error, dispatch);
    } finally {
      dispatch({ type: BATCH_TABLES_LOADING_DETAIL, payload: false });
    }

    return null;
  };
};

export const createBatchTable = (payload: CreateTablePayload) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.post('/tables', payload);

      if (response.status === 201) {
        // dispatch(showFeedbackModal({
        //   showModal: true,
        //   message: 'Table created successfully',
        //   modalType: 'success',
        //   duration: 3000,
        // }));
        return response.data;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to create table');
    }

    return null;
  };
};

export const updateBatchTable = (tableUuid: string, payload: UpdateTablePayload) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.put(`/tables/${tableUuid}`, payload);

      if (response.status === 200) {
        dispatch({ type: BATCH_TABLES_UPDATE_IN_LIST, payload: response.data });
        dispatch({ type: BATCH_TABLES_SET_DETAIL, payload: response.data });
        dispatch(showFeedbackModal({
          showModal: true,
          message: 'Table updated',
          modalType: 'success',
          duration: 2000,
        }));
        return response.data;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to update table');
    }

    return null;
  };
};

export const deleteBatchTable = (tableUuid: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.delete(`/tables/${tableUuid}`);

      if (response.status === 200) {
        dispatch({ type: BATCH_TABLES_REMOVE_FROM_LIST, payload: tableUuid });
        dispatch(showFeedbackModal({
          showModal: true,
          message: 'Table deleted',
          modalType: 'success',
          duration: 3000,
        }));
        return true;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to delete table');
    }

    return false;
  };
};

export const deleteBatchTablesBulk = (tableUuids: string[]) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis({
        method: 'POST',
        url: '/tables/delete_bulk',
        data: { table_uuids: tableUuids },
      });

      if (response.status === 200) {
        const { deleted_count, total_requested, failed } = response.data;
        const allSucceeded = deleted_count === total_requested;

        // Remove successfully deleted tables from the list
        const failedUuids = new Set((failed || []).map((f: any) => f.uuid));
        for (const uuid of tableUuids) {
          if (!failedUuids.has(uuid)) {
            dispatch({ type: BATCH_TABLES_REMOVE_FROM_LIST, payload: uuid });
          }
        }

        dispatch(showFeedbackModal({
          showModal: true,
          message: allSucceeded
            ? `${deleted_count} table${deleted_count > 1 ? 's' : ''} deleted successfully`
            : `${deleted_count} of ${total_requested} tables deleted${failed?.length ? `. ${failed.length} failed.` : ''}`,
          modalType: allSucceeded ? 'success' : 'warning',
          duration: 3000,
        }));

        return { status: 'success', data: response.data };
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to bulk delete tables');
    }
  };
};

// ─── Column Management ──────────────────────────────────────────────────────

export const addColumn = (tableUuid: string, payload: CreateColumnPayload) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.post(`/tables/${tableUuid}/columns`, payload);

      if (response.status === 201) {
        const columnData = response.data;

        // Refresh the full table to get updated columns / cells
        await dispatch(fetchBatchTableDetail(tableUuid) as any);

        // Auto-process the new column across all existing documents
        if (columnData?.id) {
          dispatch(processColumn(tableUuid, columnData.id) as any);
        }

        return columnData;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to add column');
    }
    return null;
  };
};

export const updateColumn = (tableUuid: string, columnId: number, payload: UpdateColumnPayload) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.put(`/tables/${tableUuid}/columns/${columnId}`, payload);

      if (response.status === 200) {
        // IMPORTANT: await the refetch so it completes before this action returns.
        // Without await, a subsequent processColumn() call would race with this
        // fetch — the stale response would overwrite optimistic loading state.
        await dispatch(fetchBatchTableDetail(tableUuid) as any);
        return response.data;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to update column');
    }
    return null;
  };
};

export const deleteColumn = (tableUuid: string, columnId: number) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.delete(`/tables/${tableUuid}/columns/${columnId}`);

      if (response.status === 200) {
        dispatch(fetchBatchTableDetail(tableUuid) as any);
        dispatch(showFeedbackModal({
          showModal: true,
          message: 'Column deleted',
          modalType: 'success',
          duration: 2000,
        }));
        return true;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to delete column');
    }
    return false;
  };
};

// ─── Document Upload ─────────────────────────────────────────────────────────

export const uploadDocuments = (tableUuid: string, files: File[]) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    dispatch({ type: BATCH_TABLES_SET_UPLOADING, payload: true });

    try {
      const formData = new FormData();
      files.forEach((file) => formData.append('files', file));

      const response = await apis.post(`/tables/${tableUuid}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.status === 201) {
        const uploadData = response.data;

        // Refresh the full table to get updated rows / cells
        await dispatch(fetchBatchTableDetail(tableUuid) as any);

        dispatch(showFeedbackModal({
          showModal: true,
          message: `${files.length} document${files.length > 1 ? 's' : ''} uploaded`,
          modalType: 'success',
          duration: 3000,
        }));

        // Auto-process the newly uploaded rows
        const uploadedRowIds = (uploadData.uploaded || []).map((r: any) => r.id);
        if (uploadedRowIds.length > 0) {
          dispatch(processRows(tableUuid, uploadedRowIds) as any);
        }

        return uploadData;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to upload documents');
    } finally {
      dispatch({ type: BATCH_TABLES_SET_UPLOADING, payload: false });
    }

    return null;
  };
};

export const deleteDocument = (tableUuid: string, rowId: number) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.delete(`/tables/${tableUuid}/documents/${rowId}`);

      if (response.status === 200) {
        dispatch(fetchBatchTableDetail(tableUuid) as any);
        dispatch(showFeedbackModal({
          showModal: true,
          message: 'Document removed',
          modalType: 'success',
          duration: 2000,
        }));
        return true;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to remove document');
    }
    return false;
  };
};

/**
 * Stream a table document blob for side-panel preview.
 */
export const streamTableDocument = async (tableUuid: string, rowId: number): Promise<Blob> => {
  const response = await apis.get(`/tables/${tableUuid}/documents/${rowId}/stream?disposition=inline`, {
    responseType: 'blob',
  });
  return response.data;
};

// ─── Processing ──────────────────────────────────────────────────────────────

export const startProcessing = (tableUuid: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>, getState: () => RootState) => {
    dispatch({ type: BATCH_TABLES_SET_PROCESSING, payload: true });

    // Immediately optimistically update the table status so skeleton loaders appear
    const currentTable = getState().batchTables.currentTable;
    if (currentTable && currentTable.uuid === tableUuid) {
      dispatch({
        type: BATCH_TABLES_UPDATE_TABLE_STATUS,
        payload: { uuid: tableUuid, status: 'queued' },
      });
      // Also set all pending rows to processing so skeleton loaders show on all cells
      currentTable.rows.forEach((row: any) => {
        if (row.status === 'pending') {
          dispatch({
            type: BATCH_TABLES_UPDATE_ROW_STATUS,
            payload: { row_id: row.id, status: 'processing' },
          });
        }
      });
    }

    try {
      const response = await apis.post(`/tables/${tableUuid}/process`);

      if (response.status === 202) {
        dispatch(showFeedbackModal({
          showModal: true,
          message: 'Processing started — AI is analyzing your documents',
          modalType: 'success',
          duration: 4000,
        }));

        // Safety-net refetch: don't fetch immediately (it would overwrite
        // optimistic row/cell statuses before PubSub events arrive).
        // Instead wait 15s — by then PubSub will have delivered most updates,
        // and this catches any events that were missed.
        setTimeout(() => {
          dispatch(fetchBatchTableDetail(tableUuid) as any);
        }, 15000);

        return response.data;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to start processing');
    } finally {
      dispatch({ type: BATCH_TABLES_SET_PROCESSING, payload: false });
    }

    return null;
  };
};

// ─── Partial Processing ──────────────────────────────────────────────────────

/**
 * Process a single column across all existing documents.
 * Called automatically after adding a new column.
 */
export const processColumn = (tableUuid: string, columnId: number) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>, getState: () => RootState) => {
    // Optimistic: set table to processing and mark relevant cells as pending
    const currentTable = getState().batchTables.currentTable;
    if (currentTable && currentTable.uuid === tableUuid) {
      dispatch({
        type: BATCH_TABLES_UPDATE_TABLE_STATUS,
        payload: { uuid: tableUuid, status: 'processing' },
      });
      // Reset cells for this column to 'pending' so skeleton loaders appear
      dispatch({
        type: BATCH_TABLES_RESET_COLUMN_CELLS,
        payload: { columnId },
      });
      // Mark all rows as processing so skeleton loaders show
      currentTable.rows.forEach((row: any) => {
        dispatch({
          type: BATCH_TABLES_UPDATE_ROW_STATUS,
          payload: { row_id: row.id, status: 'processing' },
        });
      });
    }

    try {
      const response = await apis.post(`/tables/${tableUuid}/process-column/${columnId}`);

      if (response.status === 202) {
        // dispatch(showFeedbackModal({
        //   showModal: true,
        //   message: 'Processing new column - analyzing your documents',
        //   modalType: 'success',
        //   duration: 3000,
        // }));

        // Safety-net refetch: if PubSub events are missed (e.g. due to race
        // condition on connect/join), refetch the full table after a delay to
        // ensure cells reflect the latest values.
        setTimeout(() => {
          dispatch(fetchBatchTableDetail(tableUuid) as any);
        }, 15000);

        return response.data;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to process column');
      // Revert optimistic update on error
      dispatch(fetchBatchTableDetail(tableUuid) as any);
    }

    return null;
  };
};

/**
 * Process specific rows (documents) across all columns.
 * Called automatically after uploading new documents.
 */
export const processRows = (tableUuid: string, rowIds: number[]) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>, getState: () => RootState) => {
    if (!rowIds.length) return null;

    // Optimistic: set table to processing and mark new rows as processing
    const currentTable = getState().batchTables.currentTable;
    if (currentTable && currentTable.uuid === tableUuid) {
      dispatch({
        type: BATCH_TABLES_UPDATE_TABLE_STATUS,
        payload: { uuid: tableUuid, status: 'processing' },
      });
      rowIds.forEach((rowId) => {
        dispatch({
          type: BATCH_TABLES_UPDATE_ROW_STATUS,
          payload: { row_id: rowId, status: 'processing' },
        });
      });
    }

    try {
      const response = await apis.post(`/tables/${tableUuid}/process-rows`, { row_ids: rowIds });

      if (response.status === 202) {
        dispatch(showFeedbackModal({
          showModal: true,
          message: `Processing ${rowIds.length} new document${rowIds.length > 1 ? 's' : ''} — AI is analyzing`,
          modalType: 'success',
          duration: 3000,
        }));

        // Safety-net refetch in case PubSub events are missed
        setTimeout(() => {
          dispatch(fetchBatchTableDetail(tableUuid) as any);
        }, 15000);

        return response.data;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to process documents');
      // Revert optimistic update on error
      dispatch(fetchBatchTableDetail(tableUuid) as any);
    }

    return null;
  };
};

// ─── Reset stuck table ──────────────────────────────────────────────────────

export const resetTable = (tableUuid: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.post(`/tables/${tableUuid}/reset`);

      if (response.status === 200) {
        dispatch(fetchBatchTableDetail(tableUuid) as any);
        dispatch(showFeedbackModal({
          showModal: true,
          message: 'Table reset — you can now re-process it',
          modalType: 'success',
          duration: 3000,
        }));
        return response.data;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to reset table');
    }

    return null;
  };
};

// ─── Export ──────────────────────────────────────────────────────────────────

export const exportTable = (tableUuid: string, format: 'json' | 'csv' = 'csv') => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.get(`/tables/${tableUuid}/export`, {
        params: { format },
        responseType: format === 'csv' ? 'blob' : 'json',
      });

      if (response.status === 200) {
        if (format === 'csv') {
          // Download as file
          const blob = new Blob([response.data], { type: 'text/csv' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `table_${tableUuid}.csv`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }

        dispatch(showFeedbackModal({
          showModal: true,
          message: 'Export complete',
          modalType: 'success',
          duration: 2000,
        }));
        return response.data;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to export table');
    }
    return null;
  };
};

// ─── Templates ───────────────────────────────────────────────────────────────

export const fetchTemplates = (category?: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    dispatch({ type: BATCH_TABLES_LOADING_TEMPLATES, payload: true });

    try {
      const response = await apis.get('/tables/templates', {
        params: category ? { category } : {},
      });

      if (response.status === 200) {
        dispatch({
          type: BATCH_TABLES_SET_TEMPLATES,
          payload: response.data.items || [],
        });
        return response.data;
      }
    } catch (error) {
      handleApiError(error, dispatch);
    } finally {
      dispatch({ type: BATCH_TABLES_LOADING_TEMPLATES, payload: false });
    }

    return null;
  };
};

export const saveTableAsTemplate = (tableUuid: string, payload: SaveAsTemplatePayload) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.post(`/tables/${tableUuid}/save-template`, payload);

      if (response.status === 201) {
        dispatch(fetchTemplates() as any);
        dispatch(showFeedbackModal({
          showModal: true,
          message: 'Template saved',
          modalType: 'success',
          duration: 3000,
        }));
        return response.data;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to save template');
    }
    return null;
  };
};

export const createTemplate = (payload: CreateTemplatePayload) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.post('/tables/templates', payload);

      if (response.status === 201) {
        dispatch(fetchTemplates() as any);
        dispatch(showFeedbackModal({
          showModal: true,
          message: 'Template created',
          modalType: 'success',
          duration: 3000,
        }));
        return response.data;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to create template');
    }
    return null;
  };
};

export const getTemplate = (templateUuid: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.get(`/tables/templates/${templateUuid}`);
      if (response.status === 200) {
        return response.data;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to load template');
    }
    return null;
  };
};

export const updateTemplate = (templateUuid: string, payload: Partial<CreateTemplatePayload>) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.put(`/tables/templates/${templateUuid}`, payload);
      if (response.status === 200) {
        dispatch(fetchTemplates() as any);
        dispatch(showFeedbackModal({
          showModal: true,
          message: 'Template updated',
          modalType: 'success',
          duration: 3000,
        }));
        return response.data;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to update template');
    }
    return null;
  };
};

export const deleteTableTemplate = (templateUuid: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.delete(`/tables/templates/${templateUuid}`);
      if (response.status === 200) {
        dispatch(fetchTemplates() as any);
        dispatch(showFeedbackModal({
          showModal: true,
          message: 'Template deleted',
          modalType: 'success',
          duration: 3000,
        }));
        return true;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to delete template');
    }
    return false;
  };
};

// ─── SSE Table Creation ──────────────────────────────────────────────────────

/**
 * Create a batch table with SSE streaming progress.
 * Sends metadata + files via multipart/form-data to /tables/create-sse,
 * reads the SSE stream, and dispatches progress events to the reducer.
 */
export const createBatchTableSSE = (
  tableData: CreateTablePayload,
  files: File[]
) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    return new Promise<any>(async (resolve, reject) => {
      dispatch({ type: BATCH_TABLE_CREATE_RESET });
      dispatch({ type: BATCH_TABLE_CREATE_CONNECTING });

      try {
        const fd = new FormData();
        fd.append('metadata', JSON.stringify(tableData));
        files.forEach((file) => fd.append('files', file));

        const resp = await fetchWithSSE(`${config.baseUrl}/tables/create-sse`, {
          method: 'POST',
          body: fd,
        });

        if (!resp.body) throw new Error('No SSE body returned');
        const reader = resp.body.getReader();
        const decoder = new TextDecoder();

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          chunk.split('\n\n').forEach((raw) => {
            if (!raw.trim()) return;

            let event = 'message';
            let dataStr = '';
            raw.split('\n').forEach((line) => {
              if (line.startsWith('event:')) event = line.slice(6).trim();
              if (line.startsWith('data:')) dataStr += line.slice(5).trim();
            });

            if (event === 'done') return;

            try {
              const parsed = dataStr ? JSON.parse(dataStr) : null;
              switch (event) {
                case 'connected':
                  dispatch({ type: BATCH_TABLE_CREATE_CONNECTED, payload: parsed });
                  break;
                case 'steps':
                  dispatch({ type: BATCH_TABLE_CREATE_STEPS, payload: parsed });
                  break;
                case 'step':
                  dispatch({ type: BATCH_TABLE_CREATE_STEP, payload: parsed });
                  break;
                case 'completed':
                  dispatch({ type: BATCH_TABLE_CREATE_COMPLETED, payload: parsed });
                  if (parsed?.ok) {
                    dispatch(showFeedbackModal({
                      showModal: true,
                      message: parsed?.message || 'Table created successfully',
                      modalType: 'success',
                      duration: 2500,
                    }));
                    resolve(parsed);
                  } else {
                    dispatch(showFeedbackModal({
                      showModal: true,
                      message: parsed?.message || 'Table creation failed.',
                      modalType: 'error',
                      duration: 3500,
                    }));
                    reject(parsed || { ok: false });
                  }
                  break;
                case 'error':
                  dispatch({ type: BATCH_TABLE_CREATE_FAILED, payload: parsed });
                  reject(parsed);
                  break;
                default:
                  break;
              }
            } catch (_e) {
              // ignore parse errors for incomplete chunks
            }
          });
        }
      } catch (err) {
        dispatch({ type: BATCH_TABLE_CREATE_FAILED, payload: { error: String(err) } });
        dispatch(showFeedbackModal({
          showModal: true,
          message: 'Table creation failed.',
          modalType: 'error',
          duration: 3500,
        }));
        reject(err);
      }
    });
  };
};

export const resetBatchTableCreation = () => (dispatch: any) => {
  dispatch({ type: BATCH_TABLE_CREATE_RESET });
};


// ─── Collaborator Management Actions ────────────────────────────────────────

export const BATCH_TABLES_SET_COLLABORATORS = 'BATCH_TABLES_SET_COLLABORATORS';

/**
 * Fetch collaborators for a table.
 */
export const fetchTableCollaborators = (tableUuid: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const res = await apis({
        method: 'GET',
        url: `/tables/${tableUuid}/collaborators`,
      });
      dispatch({ type: BATCH_TABLES_SET_COLLABORATORS, payload: res.data?.collaborators || [] });
    } catch (error: any) {
      handleApiError(error, dispatch, 'Failed to load collaborators');
    }
  };
};

/**
 * Add a collaborator to a table.
 */
export const addTableCollaborator = (tableUuid: string, collaboratorIdOrUuid: string | number, access: string = 'view') => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const data: any = { access };

      // Determine if it's a numeric ID (legacy) or a UUID string
      if (typeof collaboratorIdOrUuid === 'number' || !isNaN(Number(collaboratorIdOrUuid))) {
        data.user_id = Number(collaboratorIdOrUuid);
      } else {
        // UUID string from org-people API / CollaboratorSelector
        data.collaborator_uuid = collaboratorIdOrUuid;
      }

      await apis({
        method: 'POST',
        url: `/tables/${tableUuid}/collaborators`,
        data,
      });
      dispatch(fetchTableCollaborators(tableUuid) as any);
      dispatch(showFeedbackModal({
        showModal: true,
        message: 'Access granted',
        modalType: 'success',
        duration: 2500,
      }));
    } catch (error: any) {
      handleApiError(error, dispatch, 'Failed to grant access');
    }
  };
};

/**
 * Update a collaborator's access level.
 */
export const updateTableCollaboratorAccess = (tableUuid: string, collaboratorUuid: string, access: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      await apis({
        method: 'PUT',
        url: `/tables/${tableUuid}/collaborators/${collaboratorUuid}`,
        data: { access },
      });
      dispatch(fetchTableCollaborators(tableUuid) as any);
      // dispatch(showFeedbackModal({
      //   showModal: true,
      //   message: 'Access updated',
      //   modalType: 'success',
      //   duration: 2500,
      // }));
    } catch (error: any) {
      handleApiError(error, dispatch, 'Failed to update access');
    }
  };
};

/**
 * Remove a collaborator from a table.
 */
export const removeTableCollaborator = (tableUuid: string, collaboratorUuid: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      await apis({
        method: 'DELETE',
        url: `/tables/${tableUuid}/collaborators/${collaboratorUuid}`,
      });
      dispatch(fetchTableCollaborators(tableUuid) as any);
      dispatch(showFeedbackModal({
        showModal: true,
        message: 'Access removed',
        modalType: 'success',
        duration: 2500,
      }));
    } catch (error: any) {
      handleApiError(error, dispatch, 'Failed to remove collaborator');
    }
  };
};


// =============================================================================
// Template Collaborator Actions
// =============================================================================

/**
 * Get collaborators for a table template.
 */
export const fetchTemplateCollaborators = (templateUuid: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.get(`/tables/templates/${templateUuid}/collaborators`);
      if (response.status === 200) {
        return response.data;
      }
    } catch (error) {
      handleApiError(error, dispatch);
    }
    return null;
  };
};

/**
 * Add a collaborator to a table template.
 */
export const addTemplateCollaborator = (templateUuid: string, collaboratorIdOrUuid: string | number, access: string = 'view') => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const data: any = { access };
      if (typeof collaboratorIdOrUuid === 'number' || !isNaN(Number(collaboratorIdOrUuid))) {
        data.user_id = Number(collaboratorIdOrUuid);
      } else {
        data.collaborator_uuid = collaboratorIdOrUuid;
      }
      await apis({
        method: 'POST',
        url: `/tables/templates/${templateUuid}/collaborators`,
        data,
      });
      dispatch(showFeedbackModal({
        showModal: true,
        message: 'Access granted to template',
        modalType: 'success',
        duration: 2500,
      }));
    } catch (error: any) {
      handleApiError(error, dispatch, 'Failed to grant access');
    }
  };
};

/**
 * Update a template collaborator's access level.
 */
export const updateTemplateCollaboratorAccess = (templateUuid: string, collaboratorUuid: string, access: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      await apis({
        method: 'PUT',
        url: `/tables/templates/${templateUuid}/collaborators/${collaboratorUuid}`,
        data: { access },
      });
      // dispatch(showFeedbackModal({
      //   showModal: true,
      //   message: 'Access updated',
      //   modalType: 'success',
      //   duration: 2500,
      // }));
    } catch (error: any) {
      handleApiError(error, dispatch, 'Failed to update access');
    }
  };
};

/**
 * Remove a collaborator from a table template.
 */
export const removeTemplateCollaborator = (templateUuid: string, collaboratorUuid: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      await apis({
        method: 'DELETE',
        url: `/tables/templates/${templateUuid}/collaborators/${collaboratorUuid}`,
      });
      dispatch(showFeedbackModal({
        showModal: true,
        message: 'Access removed from template',
        modalType: 'success',
        duration: 2500,
      }));
    } catch (error: any) {
      handleApiError(error, dispatch, 'Failed to remove access');
    }
  };
};

// ─── Cell Comment Actions ────────────────────────────────────────────────────

/**
 * Fetch all comments for a specific cell.
 */
export const fetchCellComments = (tableUuid: string, cellId: number) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    dispatch({ type: BATCH_TABLES_LOADING_CELL_COMMENTS, payload: true });
    try {
      const response = await apis.get(`/tables/${tableUuid}/cells/${cellId}/comments`);
      if (response.status === 200) {
        dispatch({ type: BATCH_TABLES_SET_CELL_COMMENTS, payload: response.data });
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to load comments');
    } finally {
      dispatch({ type: BATCH_TABLES_LOADING_CELL_COMMENTS, payload: false });
    }
  };
};

/**
 * Add a comment to a cell. Updates comment_count on the cell in Redux state.
 */
export const addCellComment = (tableUuid: string, cellId: number, commentText: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.post(`/tables/${tableUuid}/cells/${cellId}/comments`, {
        comment_text: commentText,
      });
      if (response.status === 201) {
        dispatch({ type: BATCH_TABLES_ADD_CELL_COMMENT, payload: response.data });

        // Update comment_count on the cell in currentTable
        if (response.data.comment_count !== undefined) {
          dispatch({
            type: BATCH_TABLES_UPDATE_CELL,
            payload: {
              row_id: null, // Will be resolved by reducer via cell_id
              column_id: null,
              cell_id: cellId,
              partial: { comment_count: response.data.comment_count },
            },
          });
        }

        return response.data;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to add comment');
    }
    return null;
  };
};

/**
 * Update an existing cell comment. Only the author can edit.
 */
export const updateCellComment = (tableUuid: string, cellId: number, commentId: number, commentText: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.put(`/tables/${tableUuid}/cells/${cellId}/comments/${commentId}`, {
        comment_text: commentText,
      });
      if (response.status === 200) {
        dispatch({ type: BATCH_TABLES_UPDATE_CELL_COMMENT, payload: response.data });
        return response.data;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to update comment');
    }
    return null;
  };
};

/**
 * Delete a cell comment. Only the author can delete. Updates comment_count.
 */
export const deleteCellComment = (tableUuid: string, cellId: number, commentId: number) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.delete(`/tables/${tableUuid}/cells/${cellId}/comments/${commentId}`);
      if (response.status === 200) {
        dispatch({ type: BATCH_TABLES_REMOVE_CELL_COMMENT, payload: { commentId } });

        // Update comment_count on the cell in currentTable
        if (response.data.comment_count !== undefined) {
          dispatch({
            type: BATCH_TABLES_UPDATE_CELL,
            payload: {
              row_id: null,
              column_id: null,
              cell_id: cellId,
              partial: { comment_count: response.data.comment_count },
            },
          });
        }

        return true;
      }
    } catch (error) {
      handleApiError(error, dispatch, 'Failed to delete comment');
    }
    return false;
  };
};
